import { useEffect, useMemo, useRef, useState } from "react";
import type { AdminOpenClaw, AiMode, AiStatus, ProviderKeyInfo } from "../types/openclaw";
import type { AiConfigPutBody, AiConfigPutResult } from "../hooks/useAdminClaws";
import { ProviderKeyList } from "./ai-config/ProviderKeyList";
import { CapabilityList } from "./ai-config/CapabilityList";
import { RemoveKeyDialog } from "./ai-config/RemoveKeyDialog";
import styles from "./AiConfigModal.module.css";
import { APPLYING_POLL_MS, APPLYING_TIMEOUT_MS } from "./dashboard/chatgptSwitch";

const AI_ENABLED_OPTIONS = [
  { value: "enabled", label: "Enabled", description: "AI is active" },
  { value: "customer_disabled", label: "Customer disabled", description: "Customer can re-enable" },
  { value: "admin_disabled", label: "Admin disabled", description: "Customer cannot re-enable" },
] as const;

type Props = {
  open: boolean;
  // AdminOpenClaw so we have aiMode at modal-open time. Falls back to
  // "openrouter" when unset (existing rows pre-V10 migration).
  claw: AdminOpenClaw | null;
  fetchAiStatus: (tenantId: string) => Promise<AiStatus | null>;
  // Single atomic-save endpoint. Modal stages every field and submits
  // them in one PUT — no per-field live writes any more.
  onApplyAiConfig: (tenantId: string, body: AiConfigPutBody) => Promise<AiConfigPutResult>;
  onClose: () => void;
  onFlash: (message: string) => void;
};

export const AiConfigModal = ({
  open, claw, fetchAiStatus, onApplyAiConfig, onClose, onFlash,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  // The mode a save is still switching to after its request got no answer.
  const [applyingMode, setApplyingMode] = useState<AiMode | null>(null);
  const pollInFlight = useRef(false);
  // Held in a ref so a parent re-render cannot restart the confirmation poll.
  const latest = useRef({ claw, fetchAiStatus, onFlash, onClose });
  useEffect(() => {
    latest.current = { claw, fetchAiStatus, onFlash, onClose };
  });

  // Staged form state. Initialised from `claw` + `aiStatus` once they
  // load; reset every time the modal opens.
  const currentMode: AiMode = claw?.aiMode ?? "openrouter";
  const [stagedMode, setStagedMode] = useState<AiMode>(currentMode);
  const [stagedAiEnabled, setStagedAiEnabled] = useState<string>("enabled");
  const [stagedZtcManaged, setStagedZtcManaged] = useState<boolean>(false);
  // provider -> cleartext key the admin typed (will be sent on Save).
  const [stagedAdds, setStagedAdds] = useState<Record<string, string>>({});
  // providers marked for removal (will be sent on Save).
  const [stagedRemovals, setStagedRemovals] = useState<Set<string>>(new Set());
  // Required when switching from litellm -> openrouter (the new VM-side key).
  const [stagedOrKey, setStagedOrKey] = useState<string>("");

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && !d.open) {
      d.showModal();
      setError(null);
      setSubmitting(false);
      setAiStatus(null);
      setConfirmRemove(null);
      setApplyingMode(null);
      setStagedMode(currentMode);
      setStagedOrKey("");
      setStagedAdds({});
      setStagedRemovals(new Set());
    } else if (!open && d.open) {
      d.close();
      setApplyingMode(null);
    }
  }, [open, currentMode]);

  // Sync staged aiEnabled / ztcManaged from the server after fetch.
  useEffect(() => {
    if (!aiStatus) return;
    setStagedAiEnabled(aiStatus.aiEnabled);
    setStagedZtcManaged(aiStatus.ztcManaged);
  }, [aiStatus]);

  useEffect(() => {
    if (!open || !claw) return;
    let cancelled = false;
    void (async () => {
      const s = await fetchAiStatus(claw.tenantId);
      if (!cancelled) setAiStatus(s);
    })();
    return () => { cancelled = true; };
  }, [open, claw, fetchAiStatus]);

  // A mode switch runs inside the save request, and activating ChatGPT can take
  // longer than the edge allows. ztc-core carries on regardless, so a save that
  // got no answer is confirmed by reading the mode back.
  useEffect(() => {
    if (!open || applyingMode === null) return undefined;
    const startedAt = Date.now();
    let cancelled = false;
    const id = window.setInterval(() => {
      const { claw: target, fetchAiStatus: readStatus, onFlash: flash, onClose: close } = latest.current;
      if (pollInFlight.current || target === null) return;
      if (Date.now() - startedAt > APPLYING_TIMEOUT_MS) {
        setApplyingMode(null);
        setError("The switch hasn't been confirmed. Check the instance before saving again.");
        return;
      }
      pollInFlight.current = true;
      void (async () => {
        try {
          const status = await readStatus(target.tenantId);
          if (cancelled || status?.aiMode !== applyingMode) return;
          setApplyingMode(null);
          flash(`AI config saved for ${target.name}.`);
          close();
        } finally {
          pollInFlight.current = false;
        }
      })();
    }, APPLYING_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [open, applyingMode]);

  // Providers to display in the key list — current state with staging
  // applied. Pending adds appear with a placeholder masked key so the
  // admin sees they're queued; pending removals are filtered out.
  const displayedProviders = useMemo<ProviderKeyInfo[]>(() => {
    const current = aiStatus?.providers ?? [];
    const result = current.map((p): ProviderKeyInfo => {
      if (stagedRemovals.has(p.provider)) {
        return { ...p, configured: false, maskedKey: null };
      }
      if (stagedAdds[p.provider]) {
        return { ...p, configured: true, maskedKey: "**** (pending save)" };
      }
      return p;
    });
    // Stage adds for providers that aren't in the current list at all
    // (shouldn't happen given SUPPORTED_PROVIDERS, but defensive).
    Object.entries(stagedAdds).forEach(([provider, _]) => {
      if (!result.some((p) => p.provider === provider)) {
        result.push({ provider, configured: true, maskedKey: "**** (pending save)" });
      }
    });
    return result;
  }, [aiStatus, stagedAdds, stagedRemovals]);

  // Set of provider ids configured in the current staged state. Drives
  // the CapabilityList "Needs ... key" hints.
  const configuredProvidersSet = useMemo<Set<string>>(
    () => new Set(displayedProviders.filter((p) => p.configured).map((p) => p.provider)),
    [displayedProviders],
  );

  // Capability coverage as the server would compute it once Save lands,
  // given the staged provider set + staged ztcManaged. Mirrors the
  // server-side derivation in AiConfigService.getStatus so the modal
  // reflects "what you'll see after Save" without a round-trip.
  const effectiveCapabilities = useMemo(() => {
    const serverCaps = aiStatus?.capabilities ?? [];
    const haveAnyConfiguredProvider = configuredProvidersSet.size > 0;
    return serverCaps.map((c) => {
      // ztcManaged=on -> every catalog capability is covered (ZTC's keys
      // serve everything in the catalog).
      if (stagedZtcManaged) return { ...c, covered: true };
      // ztcManaged=off and no providers configured -> nothing covered.
      if (!haveAnyConfiguredProvider) return { ...c, covered: false };
      // Otherwise trust the server's last-known coverage. The server is
      // re-queried after Save so any drift settles within a save cycle.
      return c;
    });
  }, [aiStatus, configuredProvidersSet, stagedZtcManaged]);

  if (!open || !claw) return null;

  const modeChanged = stagedMode !== currentMode;
  const aiEnabledChanged = aiStatus !== null && stagedAiEnabled !== aiStatus.aiEnabled;
  const ztcManagedChanged = aiStatus !== null && stagedZtcManaged !== aiStatus.ztcManaged;
  const hasKeyAdds = Object.keys(stagedAdds).length > 0;
  const hasKeyRemovals = stagedRemovals.size > 0;
  const dirty = modeChanged || aiEnabledChanged || ztcManagedChanged || hasKeyAdds || hasKeyRemovals;

  // Mode flip from litellm -> openrouter must include a new VM-side OR key.
  const needsOrKey = modeChanged && stagedMode === "openrouter";
  const orKeyValid = !needsOrKey || stagedOrKey.trim().length > 0;
  const saveDisabled = submitting || applyingMode !== null || !dirty || !orKeyValid;

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    const body: AiConfigPutBody = {
      mode: stagedMode,
      aiEnabled: stagedAiEnabled,
      ztcManaged: stagedMode === "litellm" && stagedZtcManaged,
      ...(stagedMode === "litellm" && hasKeyAdds ? { customerKeys: stagedAdds } : {}),
      ...(stagedMode === "litellm" && hasKeyRemovals ? { removedProviders: Array.from(stagedRemovals) } : {}),
      ...(needsOrKey ? { openrouterApiKey: stagedOrKey.trim() } : {}),
    };
    const result = await onApplyAiConfig(claw.tenantId, body);
    setSubmitting(false);
    if (result.ok) {
      onFlash(`AI config saved for ${claw.name}.`);
      onClose();
    } else if (result.unconfirmed && modeChanged) {
      // No answer is not a failure here: the switch may still be landing.
      setApplyingMode(stagedMode);
    } else {
      setError(result.message);
    }
  };

  const stageAdd = (provider: string, key: string): Promise<void> => {
    setStagedAdds((prev) => ({ ...prev, [provider]: key }));
    setStagedRemovals((prev) => {
      const next = new Set(prev);
      next.delete(provider);
      return next;
    });
    return Promise.resolve();
  };

  const stageRemove = (provider: string) => {
    // If the user is staging a remove of a provider with a pending add,
    // just drop the pending add (no need to confirm — they haven't
    // committed it).
    if (stagedAdds[provider]) {
      setStagedAdds((prev) => {
        const next = { ...prev };
        delete next[provider];
        return next;
      });
      return;
    }
    // Otherwise stage the removal of an existing server-side key.
    // Confirmation dialog asks for explicit acknowledgement.
    setConfirmRemove(provider);
  };

  const configuredKeys = displayedProviders.filter((p) => p.configured);

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => { evt.preventDefault(); if (!submitting) onClose(); }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <h2 className={styles.title}>AI Configuration</h2>
          <p className={styles.subtitle}>
            <span className={styles.nameToken}>{claw.name}</span>
          </p>
        </header>

        {/* Mode selector — staged like everything else; commits via Save. */}
        <section className={styles.section}>
          <h3 className={styles.label}>AI Mode</h3>
          <label className={styles.radio}>
            <input
              type="radio" name="aiMode" value="litellm"
              checked={stagedMode === "litellm"}
              disabled={submitting}
              onChange={() => setStagedMode("litellm")}
            />
            <span>
              <strong>LiteLLM (recommended)</strong>
              <br />
              <span className={styles.hint}>Routed through ZTC's managed proxy. Per-tenant key blocking, spend tracking, customer-provided provider keys.</span>
            </span>
          </label>
          <label className={styles.radio}>
            <input
              type="radio" name="aiMode" value="openrouter"
              checked={stagedMode === "openrouter"}
              disabled={submitting}
              onChange={() => setStagedMode("openrouter")}
            />
            <span>
              <strong>Legacy (OpenRouter direct)</strong>
              <br />
              <span className={styles.hint}>OpenRouter API key on the VM. No proxy.</span>
            </span>
          </label>
          <label className={styles.radio}>
            <input
              type="radio" name="aiMode" value="chatgpt"
              checked={stagedMode === "chatgpt"}
              disabled={submitting}
              onChange={() => setStagedMode("chatgpt")}
            />
            <span>
              <strong>ChatGPT subscription</strong><br />
              <span className={styles.hint}>Uses a connected ChatGPT account. No automatic OpenRouter fallback.</span>
            </span>
          </label>
          {/* Set up by hand on an onboarding call, so it is only ever the current mode. */}
          {currentMode === "claude" && (
            <label className={styles.radio}>
              <input
                type="radio" name="aiMode" value="claude"
                checked={stagedMode === "claude"}
                disabled={submitting}
                onChange={() => setStagedMode("claude")}
              />
              <span>
                <strong>Claude Subscription</strong><br />
                <span className={styles.hint}>The customer's own Claude plan through Claude Code on the instance. Set up by hand.</span>
              </span>
            </label>
          )}
          {stagedMode === "chatgpt" && (
            <p className={styles.hint}>
              {modeChanged
                ? "Connect the ChatGPT account from the instance dashboard before switching. Saving verifies the connection."
                : "Connect or manage ChatGPT accounts from the instance dashboard. Usage limits apply to the connected subscription."}
            </p>
          )}
          {needsOrKey && (
            <div className={styles.row}>
              <input
                type="password"
                className={styles.select}
                placeholder="sk-or-v1-... (OpenRouter API key)"
                value={stagedOrKey}
                disabled={submitting}
                onChange={(e) => setStagedOrKey(e.target.value)}
                autoComplete="off"
              />
              <span className={styles.hint}>This key is verified before switching the instance to OpenRouter.</span>
            </div>
          )}
          {modeChanged && stagedMode === "litellm" && (
            <p className={styles.hint}>
              Saving will mint a fresh LiteLLM virtual key, push it to the VM, and restart the gateway.
              {currentMode === "litellm" ? " The current key will be blocked." : ""}
            </p>
          )}
        </section>

        {/* AI Access */}
        <section className={styles.section}>
          <h3 className={styles.label}>AI Access</h3>
          <div className={styles.row}>
            <select
              className={styles.select}
              value={stagedAiEnabled}
              disabled={submitting}
              onChange={(e) => setStagedAiEnabled(e.target.value)}
            >
              {AI_ENABLED_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <span className={styles.hint}>
              {AI_ENABLED_OPTIONS.find((o) => o.value === stagedAiEnabled)?.description}
            </span>
          </div>
        </section>

        {/* Provider keys / capabilities / ZTC-managed are litellm-only.
            We render them based on STAGED mode (not current) so the
            admin can stage a switch + provider keys in one Save. */}
        {stagedMode === "litellm" && (
          <>
            <section className={styles.section}>
              <h3 className={styles.label}>API Keys</h3>
              <ProviderKeyList
                providers={displayedProviders}
                submitting={submitting}
                onAdd={stageAdd}
                onRemove={stageRemove}
              />
            </section>

            <section className={styles.section}>
              <h3 className={styles.label}>Capabilities</h3>
              <CapabilityList
                capabilities={effectiveCapabilities}
                configuredProviders={configuredProvidersSet}
              />
            </section>

            <section className={styles.section}>
              <h3 className={styles.label}>
                ZTC-Managed
                <span className={styles.internalTag}>Admin only</span>
              </h3>
              <label className={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={stagedZtcManaged}
                  disabled={submitting}
                  onChange={(e) => setStagedZtcManaged(e.target.checked)}
                />
                <span>Enable ZTC-managed AI for this tenant</span>
              </label>
              <p className={styles.hint}>
                When on, capabilities the customer's keys can't serve fall through to ZTC's keys (ZTC pays).
                BYOK keys always take precedence — ZTC only fills capability gaps. When off, only customer
                keys are used; uncovered capabilities return errors.
              </p>
            </section>

            {aiStatus?.spend != null && aiStatus.spend > 0 && (
              <section className={styles.section}>
                <h3 className={styles.label}>Usage</h3>
                <div className={styles.row}>
                  <span className={styles.hint}>Spend this period</span>
                  <strong>${aiStatus.spend.toFixed(2)}</strong>
                </div>
              </section>
            )}
          </>
        )}

        {applyingMode !== null && (
          <p className={styles.hint} role="status">
            Switching the AI mode. The instance restarts to apply it, which can take a minute.
            This closes on its own once the switch is confirmed.
          </p>
        )}

        {error && <div className={styles.errorBanner}>{error}</div>}

        <footer className={styles.footer}>
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={submitting}>
            Close
          </button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleSave}
            disabled={saveDisabled}
            title={!dirty ? "Make a change to enable Save." : (!orKeyValid ? "OpenRouter key required for this switch." : undefined)}
          >
            {submitting ? "Saving…" : applyingMode !== null ? "Confirming…" : "Save"}
          </button>
        </footer>
      </div>

      {/* Remove confirmation — the user explicitly acknowledges removing
          a key. Confirming stages the removal; they still have to Save. */}
      {confirmRemove && (
        <RemoveKeyDialog
          provider={confirmRemove}
          configuredKeys={configuredKeys}
          ztcManaged={stagedZtcManaged}
          submitting={submitting}
          onConfirm={async () => {
            setStagedRemovals((prev) => new Set(prev).add(confirmRemove));
            setConfirmRemove(null);
          }}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </dialog>
  );
};
